/**
 * What Sentry may collect: the `dataCollection` option of the Sentry SDK 11+,
 * plus a `beforeSendSpan` hook for the one place the option does not reach.
 * Every `Sentry.init` in the API, the PDF Lambda and the Admin Hub and Agency
 * Portal passes both (packages/api/src/test/util/sentry-data-collection.test.ts
 * checks each one).
 *
 * Sentry 11's defaults send request and response bodies, cookies, every header,
 * AI prompts and replies, database query values, queue payloads and local
 * variables. Here those carry patient data. The rule (Nir, 2026-10-20): do not
 * restrict more than needed, keep what helps debugging, drop what carries
 * patient data.
 *
 * Kept: request and response headers that describe the request (Sentry itself
 * also drops any header, cookie or parameter whose name contains auth, token,
 * secret, session, key, cookie, password, jwt and similar), URL query
 * parameters that are ids, paging, sorting, statuses, types and dates, and the
 * parameterized SQL text of database spans (always sent; not an option).
 *
 * Dropped: request and response bodies; cookies; client IP and forwarding
 * headers; the Referer (a page URL that may hold a search); webhook
 * signatures; every other query parameter (names, phones, free-text search,
 * reasons, file paths); AI inputs and outputs; bound query values, written
 * rows and results; queue payloads; stack-frame local variables. `userInfo`
 * is off, so Sentry does not attach client IPs on its own; the web apps still
 * set the signed-in staff member explicitly (`Sentry.setUser`).
 *
 * Sentry matches these terms as case-insensitive substrings of the name.
 */
/** Header names (substrings) Sentry must not record. */
export const SENTRY_DENIED_HEADERS = [
    "forwarded",
    "-ip",
    "remote-",
    "via",
    "-user",
    "referer",
    "referrer",
    "signature",
    "x-amz-"
];
/**
 * Query parameter names (substrings) Sentry may record; all others are
 * replaced with "[Filtered]". "id" covers visitId, agencyIds, nurse_id and the
 * like: none of the API's query parameters with "id" in the name carries
 * patient data.
 */
export const SENTRY_ALLOWED_QUERY_PARAMS = [
    "id",
    "page",
    "limit",
    "offset",
    "cursor",
    "sort",
    "order",
    "dir",
    "status",
    "type",
    "kind",
    "mode",
    "date",
    "from",
    "days",
    "hours",
    "window",
    "range",
    "only",
    "include",
    "dryrun",
    "format",
    "phase",
    "section",
    "severity",
    "verdict"
];
/** Span attributes that hold a URL path; some instrumentations leave the query string in them. */
const SPAN_PATH_ATTRIBUTES = ["url.path", "http.target"];
/**
 * Pass as `beforeSendSpan` to `Sentry.init`. Sentry 11's Fastify span records
 * `url.path` with the raw query string (`/patients?q=<a name>`), which
 * `urlQueryParams` does not reach (checked against a local ingest endpoint on
 * 2026-10-20). A path never needs its query: Sentry records and filters the
 * query itself elsewhere (`url.query`, `url.full`, the event's request).
 * Span names are left alone (database span names contain `?` placeholders).
 */
export function stripQueryFromSpanPaths(span) {
    const attributes = span.attributes;
    if (attributes) {
        for (const key of SPAN_PATH_ATTRIBUTES) {
            const value = attributes[key];
            if (typeof value === "string" && value.includes("?")) {
                attributes[key] = value.slice(0, value.indexOf("?"));
            }
        }
    }
    return span;
}
/**
 * Pass as `dataCollection` to `Sentry.init`. Plain arrays and objects (not
 * `as const`) so it is assignable to Sentry's `DataCollection` type without
 * this package depending on Sentry.
 */
export const SENTRY_DATA_COLLECTION = {
    userInfo: false,
    cookies: false,
    httpHeaders: {
        request: { deny: SENTRY_DENIED_HEADERS },
        response: { deny: SENTRY_DENIED_HEADERS }
    },
    httpBodies: [],
    urlQueryParams: { allow: SENTRY_ALLOWED_QUERY_PARAMS },
    graphQL: { document: false, variables: false },
    genAI: { inputs: false, outputs: false },
    databaseQueryData: false,
    queues: false,
    stackFrameVariables: false
};
// ─── Breadcrumbs and replays ─────────────────────────────────────────────────
//
// `dataCollection` filters the event's own request, headers and spans, but not
// breadcrumbs, and not Session Replay's recording. A scan of production events
// on 2026-10-23 (counts only) found patient names and phone numbers in both:
//   - console breadcrumbs: free-text log lines, including the Twilio voice SDK
//     printing an incoming call's caller name and number;
//   - fetch/xhr/http breadcrumbs: request URLs keep their whole query string
//     (`/api/agency/cases?search=<a patient name>`, `?key=<an API key>`).
// The hooks below apply the same rules there: console lines are not sent (as
// in LogRocket, Nir 2026-10-22: log lines cannot be sanitized), and every URL
// keeps only the query parameters SENTRY_ALLOWED_QUERY_PARAMS allows, the rest
// becoming "[Filtered]" as Sentry does it.
/** What a filtered query value becomes; the same marker Sentry uses. */
export const SENTRY_FILTERED = "[Filtered]";
/** Breadcrumb categories never sent: their message is a free-text log line. */
const DROPPED_BREADCRUMB_CATEGORIES = new Set(["console"]);
/** Breadcrumb `data` keys that hold a URL (http breadcrumbs, navigations). */
const URL_DATA_KEYS = ["url", "from", "to"];
function isAllowedQueryParam(name) {
    const lower = name.toLowerCase();
    return SENTRY_ALLOWED_QUERY_PARAMS.some((term) => lower.includes(term));
}
/**
 * A query string (without the `?`) with every value whose parameter is not
 * allowed replaced. Names stay, so a breadcrumb still shows which filters a
 * request used. Text that is not `name=value` pairs is filtered whole.
 */
export function filterSentryQuery(query) {
    return query
        .split("&")
        .map((pair) => {
        if (pair === "") {
            return pair;
        }
        const eq = pair.indexOf("=");
        if (eq <= 0) {
            return SENTRY_FILTERED;
        }
        const name = pair.slice(0, eq);
        let decoded = name;
        try {
            decoded = decodeURIComponent(name.replace(/\+/g, " "));
        }
        catch {
            // A malformed name is matched as written.
        }
        return isAllowedQueryParam(decoded) ? pair : `${name}=${SENTRY_FILTERED}`;
    })
        .join("&");
}
/**
 * A URL, path or any text with every query string in it filtered and every
 * fragment dropped. Never throws.
 *
 *   /api/agency/cases?search=Jane&page=2 → /api/agency/cases?search=[Filtered]&page=2
 */
export function filterSentryUrl(text) {
    return text.replace(/\?([^\s"'<>#]*)(#[^\s"'<>]*)?/g, (_match, query) => {
        return query.includes("=") ? `?${filterSentryQuery(query)}` : `?${query}`;
    });
}
/**
 * Pass as `beforeBreadcrumb` to `Sentry.init` (or call it from an existing
 * hook). Drops console breadcrumbs; filters the query string of every URL in
 * an http or navigation breadcrumb and in any message. Returns the breadcrumb
 * it was given (changed in place) or null.
 */
export function scrubSentryBreadcrumb(breadcrumb) {
    const crumb = breadcrumb;
    if (!crumb || typeof crumb !== "object") {
        return breadcrumb;
    }
    if (crumb.category && DROPPED_BREADCRUMB_CATEGORIES.has(crumb.category)) {
        return null;
    }
    if (typeof crumb.message === "string") {
        crumb.message = filterSentryUrl(crumb.message);
    }
    const data = crumb.data;
    if (data && typeof data === "object") {
        for (const key of URL_DATA_KEYS) {
            if (typeof data[key] === "string") {
                data[key] = filterSentryUrl(data[key]);
            }
        }
        // The React Native SDK records the query and fragment beside the URL.
        if (typeof data["http.query"] === "string") {
            data["http.query"] = filterSentryQuery(data["http.query"].replace(/^\?/, ""));
        }
        delete data["http.fragment"];
    }
    return breadcrumb;
}
/**
 * Pass as `beforeSend` in the nurse app. The iOS and Android SDKs record their
 * own network breadcrumbs (native image loads such as a static map with a visit
 * marker, SDK uploads) and merge them into the event without going through
 * `beforeBreadcrumb`; seen in production on 2026-10-23. This applies the same
 * scrub to every breadcrumb on the event just before it is sent. Events the
 * native layer sends on its own (native crashes) do not pass through here.
 */
export function scrubSentryEventBreadcrumbs(event) {
    const e = event;
    if (e && Array.isArray(e.breadcrumbs)) {
        e.breadcrumbs = e.breadcrumbs
            .map((b) => scrubSentryBreadcrumb(b))
            .filter((b) => b !== null);
    }
    return event;
}
/**
 * Pass as `beforeAddRecordingEvent` to `Sentry.replayIntegration`. A replay
 * carries its own copy of each breadcrumb (already through
 * `scrubSentryBreadcrumb`; checked again here) and a performance span for
 * every page load and request, whose description is the full URL.
 */
export function scrubReplayRecordingEvent(event) {
    const data = event?.data;
    if (!data || typeof data !== "object" || !data.payload) {
        return event;
    }
    if (data.tag === "breadcrumb") {
        return scrubSentryBreadcrumb(data.payload) === null ? null : event;
    }
    if (data.tag === "performanceSpan" && typeof data.payload.description === "string") {
        data.payload.description = filterSentryUrl(data.payload.description);
    }
    return event;
}
/**
 * Session Replay options for the Admin Hub and Agency Portal. Text, inputs and
 * media are masked (Sentry's defaults, written out so a default change cannot
 * turn them off), no request or response details are captured (no
 * `networkDetailAllowUrls`), and recording events go through
 * `scrubReplayRecordingEvent`.
 */
export function sentryReplayOptions() {
    return {
        maskAllText: true,
        maskAllInputs: true,
        blockAllMedia: true,
        networkDetailAllowUrls: [],
        beforeAddRecordingEvent: scrubReplayRecordingEvent
    };
}
//# sourceMappingURL=sentry-data-collection.js.map